import {
	CheckCircle2,
	Clock,
	Pencil,
	Trash2,
	TrendingUp,
	TriangleAlert,
} from "lucide-react";
import { Card, CardContent } from "../card";
import { useCurrency } from "../dropdown";
import { cn } from "../../utilities/helpers";

// Goal figures are targets, not ledger entries: cents are noise at this scale
// and they crowd the one number the page exists to answer. formatCurrency in
// home/shared always prints two decimals, so whole-unit money lives here as a
// presentation concern.
export const formatWholeCurrency = (baseValue, currency) => {
	const amount = Number(baseValue || 0);
	const currencyCode = currency?.name || "USD";

	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "symbol",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	} catch {
		return `${currencyCode} ${Math.round(amount)}`;
	}
};

export const formatSignedWholeCurrency = (baseValue, currency) => {
	const amount = Number(baseValue || 0);
	const formatted = formatWholeCurrency(Math.abs(amount), currency);
	if (Math.abs(amount) < 0.5) return formatted;
	return `${amount > 0 ? "+" : "−"}${formatted}`;
};

// timeZone: "UTC" matters -- the investment-year boundaries are constructed with
// Date.UTC, so without it a user west of UTC sees "11 Dec" render as the 10th.
export const formatGoalDate = (date) =>
	date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

export const formatDuration = (months) => {
	const total = Math.max(0, Math.round(Number(months) || 0));
	if (total < 1) return "less than a month";
	if (total < 24) return `${total} month${total === 1 ? "" : "s"}`;
	const years = Math.floor(total / 12);
	const rest = total % 12;
	return rest ? `${years} yr ${rest} mo` : `${years} yr`;
};

// One decimal is all the precision the rate deserves -- it is an assumption,
// not a measurement.
export const formatRate = (annualPercent) => `${Number(annualPercent || 0).toFixed(1)}%/yr`;

// Every surface names the two scenarios the same way, because the whole point
// is that the reader can tell at a glance which number rests on an assumption
// and which on this portfolio's own history.
export const SCENARIO_META = {
	baseline: {
		label: "Fair estimate",
		describe: (percent) =>
			`A plain long-run assumption of ${formatRate(percent)}, independent of how this portfolio has done.`,
	},
	historical: {
		label: "Your past return",
		describe: (percent, usesMoneyWeighted) =>
			usesMoneyWeighted
				? `${formatRate(percent)}, the annualized return on the money you have actually invested (XIRR). Past years were exceptional; they may not repeat.`
				: `${formatRate(percent)}, the compound annual return since the first trade. Past years were exceptional; they may not repeat.`,
	},
};

// Ordered so the assumption the headline figures use comes first.
export const listScenarios = (row) => [
	{ key: "baseline", ...SCENARIO_META.baseline, row: row?.scenarios?.baseline },
	{ key: "historical", ...SCENARIO_META.historical, row: row?.scenarios?.historical },
];

// Never round 99.6% up to "100% of target" while the goal is still short.
export const formatProgressPercent = (percent) => {
	const value = Number(percent) || 0;
	if (value >= 100) return `${Math.round(value)}%`;
	return `${Math.floor(value)}%`;
};

// Every status pairs a hue with its own icon and words, so the state survives
// greyscale, colour-blindness and a glance. `track` is a lighter step of the
// same hue as `fill`, and `projected` sits between the two.
export const STATUS_META = {
	ACHIEVED: {
		label: "Achieved",
		Icon: CheckCircle2,
		badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
		fill: "bg-emerald-600",
		projected: "bg-emerald-300",
		track: "bg-emerald-100",
	},
	ON_TRACK: {
		label: "On track",
		Icon: TrendingUp,
		badge: "border-sky-200 bg-sky-50 text-sky-700",
		fill: "bg-sky-600",
		projected: "bg-sky-300",
		track: "bg-sky-100",
	},
	BEHIND: {
		label: "Behind",
		Icon: TriangleAlert,
		badge: "border-amber-200 bg-amber-50 text-amber-800",
		fill: "bg-amber-500",
		projected: "bg-amber-200",
		track: "bg-amber-100",
	},
	EXPIRED: {
		label: "Deadline passed",
		Icon: Clock,
		badge: "border-slate-200 bg-slate-100 text-slate-700",
		fill: "bg-slate-500",
		projected: "bg-slate-300",
		track: "bg-slate-200",
	},
};

export const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.BEHIND;

export const StatusBadge = ({ status, className }) => {
	const meta = getStatusMeta(status);
	const { Icon } = meta;

	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
				meta.badge,
				className
			)}
		>
			<Icon className="h-3.5 w-3.5" aria-hidden="true" />
			{meta.label}
		</span>
	);
};

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

// Two stacked segments: where the portfolio is today, and where growth alone
// carries it by the deadline. The space still showing the track is exactly the
// part that new contributions have to cover.
export const GoalProgressBar = ({ row, className }) => {
	const meta = getStatusMeta(row.status);
	const current = clampPercent(row.progressPercent);
	const projected = clampPercent(row.projectedPercent);
	const showProjected = row.status !== "EXPIRED" && projected > current + 0.5;

	return (
		<div
			className={cn("relative h-2.5 w-full overflow-hidden rounded-full", meta.track, className)}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(current)}
			aria-valuetext={`${formatProgressPercent(row.progressPercent)} of target`}
		>
			{showProjected && (
				<div
					className={cn("absolute inset-y-0 left-0 rounded-full", meta.projected)}
					style={{ width: `${projected}%` }}
				/>
			)}
			<div
				className={cn("absolute inset-y-0 left-0 rounded-full", meta.fill)}
				style={{ width: `${current}%` }}
			/>
		</div>
	);
};

const IconButton = ({ label, Icon, onClick, className }) => (
	<button
		type="button"
		onClick={onClick}
		aria-label={label}
		title={label}
		className={cn(
			"inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
			className
		)}
	>
		<Icon className="h-4 w-4" aria-hidden="true" />
	</button>
);

const Stat = ({ label, value, swatch, tone }) => (
	<div className="min-w-0">
		<dt className="flex items-center gap-1.5 text-xs text-slate-500">
			{swatch && (
				<span className={cn("h-2 w-2 shrink-0 rounded-full", swatch)} aria-hidden="true" />
			)}
			<span className="truncate">{label}</span>
		</dt>
		<dd
			className={cn(
				"mt-1 text-sm font-semibold tabular-nums text-slate-900",
				tone === "positive" && "text-emerald-700",
				tone === "negative" && "text-rose-700"
			)}
		>
			{value}
		</dd>
	</div>
);

// The card answers one question first: what do I have to put in each month?
// Everything else is evidence for that number.
const buildAnswer = (row, money, signedMoney) => {
	const rate = formatRate(row.annualPercent);
	const deadline = formatGoalDate(row.deadline);

	if (row.status === "EXPIRED") {
		return {
			label: "Deadline passed",
			value: deadline,
			caption:
				"Figures below compare today's portfolio value with the target, not its value on the deadline.",
		};
	}

	if (row.monthsRemaining < 0.1 && !row.onTrack) {
		return {
			label: "Lump sum needed now",
			value: money(row.requiredMonthly),
			caption: `The deadline is here, so only a one-off top-up closes the gap to ${money(row.target)}.`,
		};
	}

	if (row.onTrack) {
		return {
			label: "Invest each month",
			value: money(0),
			unit: "/mo",
			caption: `Nothing extra needed: at the fair estimate of ${rate}, growth alone projects ${signedMoney(row.surplus)} above target by ${deadline}.`,
		};
	}

	return {
		label: "Invest each month",
		value: money(row.requiredMonthly),
		unit: "/mo",
		caption: `For the next ${formatDuration(row.monthsRemaining)} to reach ${money(row.target)} by ${deadline}, at the fair estimate of ${rate}.`,
	};
};

// The headline above rests on one growth assumption. This is where the other
// one gets equal billing: same goal, same deadline, solved at the rate this
// portfolio has actually returned. A wide gap between the two rows is the
// honest signal that the plan hinges on the assumption, not on the target.
const ScenarioComparison = ({ row, money, usesMoneyWeighted }) => (
	<div className="overflow-hidden rounded-lg border border-slate-200">
		<table className="w-full border-collapse text-sm">
			<caption className="sr-only">
				Required monthly investment and projected value under each growth assumption
			</caption>
			<thead>
				<tr className="bg-slate-50 text-xs font-medium text-slate-500">
					<th scope="col" className="px-3 py-2 text-left">
						Growth assumption
					</th>
					<th scope="col" className="px-3 py-2 text-right">
						Invest each month
					</th>
					<th scope="col" className="px-3 py-2 text-right">
						Projected by {row.goal.year}
					</th>
				</tr>
			</thead>
			<tbody>
				{listScenarios(row).map(({ key, label, describe, row: scenario }) => (
					<tr key={key} className="border-t border-slate-200">
						<th scope="row" className="px-3 py-2 text-left font-normal">
							<span
								className="font-medium text-slate-900"
								title={describe(scenario.annualPercent, usesMoneyWeighted)}
							>
								{label}
							</span>
							<span className="ml-1.5 tabular-nums text-slate-500">
								{formatRate(scenario.annualPercent)}
							</span>
						</th>
						<td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
							{scenario.onTrack ? "None needed" : `${money(scenario.requiredMonthly)}/mo`}
						</td>
						<td className="px-3 py-2 text-right tabular-nums text-slate-600">
							{money(scenario.projectedValue)}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

const GoalCard = ({ row, contextLabel, usesMoneyWeighted, onEdit, onDelete }) => {
	const { currency } = useCurrency();
	const meta = getStatusMeta(row.status);

	const money = (value) => formatWholeCurrency(value, currency);
	const signedMoney = (value) => formatSignedWholeCurrency(value, currency);

	const isFinal = row.goal.type === "FINAL";
	const title = isFinal ? "Final goal" : `${row.goal.year} goal`;
	const answer = buildAnswer(row, money, signedMoney);

	return (
		<Card className="flex h-full flex-col rounded-xl border-slate-200 bg-white shadow-sm">
			<CardContent className="flex flex-1 flex-col gap-5 p-5">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h2 className="text-base font-semibold text-slate-900">{title}</h2>
							<StatusBadge status={row.status} />
						</div>
						<p className="mt-1 text-sm text-slate-500">
							{contextLabel ? `${contextLabel} · ` : ""}
							due {formatGoalDate(row.deadline)}
						</p>
						{row.goal.note && (
							<p className="mt-1 truncate text-sm text-slate-600">{row.goal.note}</p>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-1.5">
						<IconButton
							label={`Edit ${title}`}
							Icon={Pencil}
							onClick={() => onEdit(row.goal)}
						/>
						<IconButton
							label={`Delete ${title}`}
							Icon={Trash2}
							onClick={() => onDelete(row.goal)}
							className="hover:bg-rose-50 hover:text-rose-700"
						/>
					</div>
				</div>

				<div className="rounded-lg bg-slate-50 px-4 py-3">
					<p className="text-sm text-slate-500">{answer.label}</p>
					<p className="mt-0.5 text-3xl font-semibold text-slate-900">
						{answer.value}
						{answer.unit && (
							<span className="ml-1 text-base font-medium text-slate-500">
								{answer.unit}
							</span>
						)}
					</p>
					<p className="mt-1.5 text-sm leading-5 text-slate-500">{answer.caption}</p>
				</div>

				{row.status !== "EXPIRED" && (
					<ScenarioComparison
						row={row}
						money={money}
						usesMoneyWeighted={usesMoneyWeighted}
					/>
				)}

				<div>
					<div className="mb-2 flex items-baseline justify-between gap-3 text-sm">
						<span className="font-medium text-slate-900">
							{formatProgressPercent(row.progressPercent)} of target
						</span>
						<span className="text-slate-500">
							{formatDuration(row.monthsRemaining)}{" "}
							{row.status === "EXPIRED" ? "overdue" : "left"}
						</span>
					</div>
					<GoalProgressBar row={row} />
				</div>

				<dl className="mt-auto grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
					<Stat label="Target" value={money(row.target)} />
					<Stat label="Today" value={money(row.currentWorth)} swatch={meta.fill} />
					<Stat
						label={
							row.status === "EXPIRED"
								? "No projection"
								: `Projected at ${formatRate(row.annualPercent)}`
						}
						value={row.status === "EXPIRED" ? "—" : money(row.projectedValue)}
						swatch={row.status === "EXPIRED" ? null : meta.projected}
					/>
					{row.onTrack ? (
						<Stat
							label="Surplus at that rate"
							value={signedMoney(row.surplus)}
							tone="positive"
						/>
					) : (
						<Stat
							label="Shortfall at that rate"
							value={signedMoney(-row.projectedGap)}
							tone="negative"
						/>
					)}
				</dl>
			</CardContent>
		</Card>
	);
};

export default GoalCard;
